import Checkbox from "@/Components/Form/Checkbox";
import GuestLayout from "@/Layouts/GuestLayout";
import InputError from "@/Components/Form/InputError";
import InputLabel from "@/Components/Form/InputLabel";
import PrimaryButton from "@/Components/Form/Buttons/PrimaryButton";
import TextInput from "@/Components/Form/TextInput";
import { Head, Link, useForm } from "@inertiajs/react";
import { login as loginRoute } from "@/routes";
import { request as passwordRequest } from "@/routes/password";
import type { FormEvent } from "react";

interface LoginProps {
    canResetPassword?: boolean;
    status?: string;
}

export default function Login({ canResetPassword, status }: LoginProps) {
    const { data, setData, post, processing, errors, reset } = useForm({
        email: "",
        password: "",
        remember: false,
    });

    const submit = (event: FormEvent) => {
        event.preventDefault();

        post(loginRoute.url(), {
            onFinish: () => {
                reset("password");
            },
        });
    };

    return (
        <GuestLayout>
            <Head title="Log in" />

            {status ? <div className="mb-4 text-sm font-medium text-teal-600">{status}</div> : null}

            <form onSubmit={submit}>
                <div>
                    <InputLabel value="Email" />

                    <TextInput
                        id="email"
                        type="email"
                        className="mt-1 block w-full"
                        value={data.email}
                        onChange={(e) => setData("email", e.target.value)}
                        required
                        autoFocus
                        autoComplete="username"
                    />

                    <InputError className="mt-2" message={errors.email} />
                </div>

                <div className="mt-4">
                    <InputLabel value="Password" />

                    <TextInput
                        id="password"
                        type="password"
                        className="mt-1 block w-full"
                        value={data.password}
                        onChange={(e) => setData("password", e.target.value)}
                        required
                        autoComplete="current-password"
                    />

                    <InputError className="mt-2" message={errors.password} />
                </div>

                <div className="mt-4 block">
                    <label className="flex items-center">
                        <Checkbox
                            name="remember"
                            checked={data.remember}
                            onChange={(e) => setData("remember", e.target.checked)}
                        />
                        <span className="ms-2 text-sm text-gray-300">Remember me</span>
                    </label>
                </div>

                <div className="mt-4 flex items-center justify-end">
                    {canResetPassword ? (
                        <Link
                            href={passwordRequest.url()}
                            className="focus:ring-offset-dark-surface-elevated rounded-md text-sm text-gray-400 underline hover:text-gray-200 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:outline-none"
                        >
                            Forgot your password?
                        </Link>
                    ) : null}

                    <PrimaryButton className={`ms-4 ${processing ? "opacity-25" : ""}`} disabled={processing}>
                        Log in
                    </PrimaryButton>
                </div>
            </form>
        </GuestLayout>
    );
}
